// app/src/modules/ui/DocumentViewer.tsx
// One in-app viewer for every printable document or file. Instead of popping a new
// window or opening a tab in Safari (which kicks you out of the PWA with no way
// back), documents render in a centered in-app modal (dimmed backdrop, tap outside
// or "Close" to dismiss) with a "Print / Save PDF" action. Mount <DocumentViewerHost/>
// once near the app root, then call openDocument(...) from anywhere.
import { useEffect, useRef, useState } from "react";
import { printHtml, printReceipt, type ReceiptData } from "../../lib/receipt";

/** A self-contained HTML document (rendered + printable) or a file URL (image/PDF). */
export type DocSource =
  | { title: string; html: string; receipt?: ReceiptData }
  | { title: string; src: string; downloadName?: string };

/** Open a document in the shared in-app viewer from anywhere in the app. */
export function openDocument(doc: DocSource) {
  window.dispatchEvent(new CustomEvent("epos:open-doc", { detail: doc }));
}

function DocumentViewer({ doc, onClose }: { doc: DocSource; onClose: () => void }) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const isHtml = "html" in doc;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // For HTML docs, size the iframe to its content so the overlay (not the iframe)
  // scrolls — iOS Safari doesn't scroll inside iframes reliably.
  function fitToContent() {
    const f = frameRef.current;
    try {
      const h = f?.contentWindow?.document?.body?.scrollHeight;
      if (f && h) f.style.height = `${h + 24}px`;
    } catch { /* cross-origin file — leave at full height */ }
  }

  return (
    <div style={S.scrim} onClick={onClose}>
      <div style={S.modal} role="dialog" aria-modal="true" aria-label={doc.title} onClick={(e) => e.stopPropagation()}>
        <div style={S.bar}>
          <button style={S.back} onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
            Close
          </button>
          <span style={S.title}>{doc.title}</span>
          {isHtml
            ? <button style={S.action} onClick={() => { if ("receipt" in doc && doc.receipt) { void printReceipt(doc.receipt); } else { printHtml(doc.html); } }}>Print / Save PDF</button>
            : <a style={S.action} href={doc.src} download={doc.downloadName ?? ""}>Download</a>}
        </div>
        <div style={S.body}>
          {isHtml
            ? <iframe ref={frameRef} title={doc.title} srcDoc={doc.html} style={S.htmlFrame} onLoad={fitToContent} />
            : <iframe ref={frameRef} title={doc.title} src={doc.src} style={S.fileFrame} />}
        </div>
      </div>
    </div>
  );
}

/** Mounted once near the app root; shows the viewer whenever openDocument() fires. */
export default function DocumentViewerHost() {
  const [doc, setDoc] = useState<DocSource | null>(null);
  useEffect(() => {
    const on = (e: Event) => setDoc((e as CustomEvent).detail as DocSource);
    window.addEventListener("epos:open-doc", on);
    return () => window.removeEventListener("epos:open-doc", on);
  }, []);
  if (!doc) return null;
  return <DocumentViewer doc={doc} onClose={() => setDoc(null)} />;
}

const S: Record<string, React.CSSProperties> = {
  scrim: { position: "fixed", inset: 0, zIndex: 200, background: "rgba(20,18,16,.55)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "clamp(10px,3vw,28px)", animation: "fade-in .2s ease" },
  modal: { display: "flex", flexDirection: "column", width: "min(860px, 100%)", maxHeight: "min(92vh, 1000px)", background: "var(--paper)", borderRadius: 4, overflow: "hidden", boxShadow: "0 24px 64px rgba(0,0,0,.45)", border: "1px solid var(--line)" },
  bar: {
    display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
    padding: "12px clamp(12px,3vw,18px)",
    background: "var(--card)", borderBottom: "1px solid var(--line)",
  },
  back: {
    display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
    background: "var(--paper-2)", border: "1px solid var(--line)", color: "var(--ink)",
    fontWeight: 700, fontSize: 14, padding: "8px 13px", borderRadius: 4,
  },
  title: { flex: 1, textAlign: "center", fontWeight: 700, fontSize: 15, color: "var(--ink-2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  action: {
    display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", textDecoration: "none", whiteSpace: "nowrap",
    background: "var(--accent)", color: "var(--accent-ink)", fontWeight: 700, fontSize: 14, padding: "9px 16px", borderRadius: 4,
  },
  body: { flex: 1, minHeight: 0, overflow: "auto", WebkitOverflowScrolling: "touch", background: "#525659" },
  htmlFrame: { width: "100%", minHeight: "100%", border: 0, display: "block", background: "#fff" },
  fileFrame: { width: "100%", height: "100%", border: 0, display: "block", background: "#fff" },
};
